import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePopupMessages, calculateUserStats } from '@/hooks/usePopupMessages';
import { PopupMessage } from '@/components/PopupMessage';
import { getProfile, getBaselineAssessmentResponses } from '@/lib/api';
import { useProductContext } from '@/contexts/ProductContext';

interface PopupMessageContextValue {
  checkTrigger: (triggerType: string, context?: Record<string, number>) => Promise<void>;
}

const PopupMessageContext = createContext<PopupMessageContextValue | null>(null);

export function usePopupMessageContext() {
  const context = useContext(PopupMessageContext);
  if (!context) {
    // Return a no-op function if used outside provider (e.g., during SSR or before provider mounts)
    return { checkTrigger: async () => {} };
  }
  return context;
}

interface PopupMessageProviderProps {
  children: ReactNode;
}

export function PopupMessageProvider({ children }: PopupMessageProviderProps) {
  const { currentProduct } = useProductContext();

  const [userContext, setUserContext] = useState<{
    userId: string;
    userName?: string;
    quitDate?: string;
    cigarettesPerDay?: number;
    costPerPack?: number;
    language?: string;
    daysSmokeFree?: number;
    moneySaved?: number;
    cigarettesAvoided?: number;
    programProgress?: number;
    currentPhase?: string;
    productId?: string;
  } | null>(null);

  const { currentMessage, showMessage, dismissMessage, checkTrigger } = usePopupMessages(userContext);

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setUserContext(null);
        return;
      }

      // Get universal profile data (name, language)
      const profileResult = await getProfile(user.id);
      const profile = profileResult.success ? profileResult.data : null;

      // Get baseline assessment responses for MindCotine product
      const baselineResult = await getBaselineAssessmentResponses(user.id, 'mindcotine');
      const baselineData = baselineResult.success ? baselineResult.data : {};

      // Calculate user stats from baseline assessment data
      const stats = calculateUserStats({
        quit_date: baselineData.quit_date || null,
        cigarettes_per_day: baselineData.cigarettes_per_day ? Number(baselineData.cigarettes_per_day) : null,
        cost_per_pack: baselineData.cost_per_pack ? Number(baselineData.cost_per_pack) : null,
      });

      // Get program progress
      const { data: enrollment } = await supabase
        .from('user_program_enrollments')
        .select(`
          *,
          program_phases!current_phase_id(name)
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      let programProgress = 0;
      let currentPhase = 'Getting Started';

      if (enrollment) {
        // Calculate progress
        const { data: completions } = await supabase
          .from('user_activity_completions')
          .select('id')
          .eq('user_id', user.id)
          .eq('status', 'completed');

        const { data: totalActivities } = await supabase
          .from('phase_activities')
          .select('id, program_phases!inner(program_id)')
          .eq('program_phases.program_id', enrollment.program_id);

        if (totalActivities && totalActivities.length > 0) {
          programProgress = Math.round(((completions?.length || 0) / totalActivities.length) * 100);
        }

        if (enrollment.program_phases) {
          currentPhase = (enrollment.program_phases as { name: string }).name;
        }
      }

      setUserContext({
        userId: user.id,
        userName: profile?.full_name || undefined,
        quitDate: baselineData.quit_date,
        cigarettesPerDay: baselineData.cigarettes_per_day ? Number(baselineData.cigarettes_per_day) : undefined,
        costPerPack: baselineData.cost_per_pack ? Number(baselineData.cost_per_pack) : undefined,
        language: profile?.preferred_language || 'en',
        daysSmokeFree: stats.daysSmokeFree,
        moneySaved: stats.moneySaved,
        cigarettesAvoided: stats.cigarettesAvoided,
        programProgress,
        currentPhase,
        productId: currentProduct,
      });
    };

    loadUserData();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      loadUserData();
    });

    return () => subscription.unsubscribe();
  }, [currentProduct]);

  // Check milestone triggers on load - only trigger the HIGHEST reached milestone
  useEffect(() => {
    if (!userContext?.userId) return;

    // Find the highest smoke-free milestone reached
    const milestones = [1, 3, 7, 14, 21, 30, 60, 90, 180, 365];
    const daysSmokeFree = userContext.daysSmokeFree || 0;
    
    // Only trigger for the exact milestone the user has reached (not passed)
    const exactMilestone = milestones.find(m => daysSmokeFree === m);
    if (exactMilestone) {
      checkTrigger('smoke_free_days', { days: exactMilestone });
    }

    // Find the highest money milestone reached
    const moneyMilestones = [10, 25, 50, 100, 250, 500, 1000];
    const moneySaved = userContext.moneySaved || 0;
    
    // Only trigger for exact milestone matches
    const exactMoneyMilestone = moneyMilestones.find(m => Math.floor(moneySaved) === m);
    if (exactMoneyMilestone) {
      checkTrigger('money_saved', { amount: exactMoneyMilestone });
    }
  }, [userContext?.userId, userContext?.daysSmokeFree, userContext?.moneySaved]);

  return (
    <PopupMessageContext.Provider value={{ checkTrigger }}>
      {children}
      {currentMessage && (
        <PopupMessage
          open={showMessage}
          onClose={dismissMessage}
          message={currentMessage}
        />
      )}
    </PopupMessageContext.Provider>
  );
}
